"use client";

import { useBooking } from "@/contexts/BookingContext";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  CreditCard,
  Wallet,
  AlertCircle,
  CheckCircle,
  Crown,
  X,
  Building2,
  Hash,
  Smartphone,
} from "lucide-react";
import { BookingStep } from "@/types/booking";
import { useState, useEffect, useCallback, useRef } from "react";
import { useAvailabilityPolling } from "@/hooks/useAvailabilityPolling";
import { useSlotReservationTimer } from "@/hooks/useSlotReservationTimer";
import { SlotCountdownTimer } from "@/components/ui/SlotCountdownTimer";
import {
  useCreateAppointment,
  useAppointmentPreview,
} from "@/lib/apollo/hooks/booking-edge";
import { formatPrice } from "@/lib/utils";
import { graphqlFetch } from "@/lib/graphql-client";
import { DELETE_APPOINTMENT } from "@/lib/apollo/queries/booking-edge";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import CartSummary from "../CartSummary";
import {
  useTriggerFormAfterAppointment,
  useSubmitWorkflowAnswers,
} from "@/lib/api/useWorkflowForms";
// Script tag functionality will be handled differently in Vite

declare global {
  interface Window {
    PaystackPop: any;
  }
}

// PaystackPop v2 instance type for Apple Pay support
interface PaystackPopInstance {
  checkout: (options: {
    key: string;
    email: string;
    amount: number;
    currency?: string;
    ref?: string;
    accessCode?: string;
    channels?: string[];
    onSuccess: (transaction: { reference: string }) => void;
    onCancel: () => void;
    onClose?: () => void;
  }) => Promise<void>;
  paymentRequest: (options: {
    key: string;
    email: string;
    amount: number;
    currency?: string;
    ref?: string;
    accessCode?: string;
    container: string;
    loadPaystackCheckoutButton?: string;
    style?: {
      theme?: "dark" | "light";
      applePay?: {
        margin?: string;
        padding?: string;
        width?: string;
        borderRadius?: string;
        type?: string;
        locale?: string;
      };
    };
    onSuccess: (transaction: { reference: string }) => void;
    onError?: () => void;
    onCancel: () => void;
    onElementsMount?: (elements: { applePay: boolean } | null) => void;
  }) => Promise<void>;
}

type PaymentMethod =
  | "card"
  | "cash"
  | "deposit"
  | "wallet"
  | "membership_wallet"
  | "voucher"
  | "unpaid";

type PaymentProvider = "paystack" | "pocket";
type PaymentOption = "card" | "transfer" | "bank" | "ussd" | "pocket_app";

// Map payment options to their provider and Paystack channel
const PAYMENT_OPTION_CONFIG: Record<PaymentOption, { provider: PaymentProvider; channels?: string[] }> = {
  card: { provider: "paystack", channels: ["card"] },
  transfer: { provider: "pocket" },
  bank: { provider: "paystack", channels: ["bank_transfer"] },
  ussd: { provider: "paystack", channels: ["ussd"] },
  pocket_app: { provider: "pocket" },
};

// localStorage key for persisting held appointment info across page reloads.
// If the user closes the tab or refreshes mid-checkout, we can still clean up
// the stale appointment the next time they start a new booking.
const HELD_APPOINTMENT_KEY = "splice_held_appointment";

interface HeldAppointmentRecord {
  appointmentId: string;
  salonId: string;
}

function saveHeldAppointment(appointmentId: string, salonId: string) {
  try {
    localStorage.setItem(
      HELD_APPOINTMENT_KEY,
      JSON.stringify({ appointmentId, salonId })
    );
  } catch {
    // localStorage may be unavailable (private browsing, storage full, etc.)
  }
}

function clearHeldAppointment() {
  try {
    localStorage.removeItem(HELD_APPOINTMENT_KEY);
  } catch {
    // Ignore — best effort
  }
}

function getHeldAppointment(): HeldAppointmentRecord | null {
  try {
    const raw = localStorage.getItem(HELD_APPOINTMENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.appointmentId && parsed?.salonId) return parsed;
    return null;
  } catch {
    return null;
  }
}

/**
 * Delete a previously held appointment found in localStorage.
 * Fire-and-forget — errors are logged but don't block the caller.
 */
async function deleteStaleHeldAppointment() {
  const stale = getHeldAppointment();
  if (!stale) return;

  clearHeldAppointment();
  try {
    await graphqlFetch(DELETE_APPOINTMENT, {
      salonId: stale.salonId,
      id: stale.appointmentId,
    });
  } catch (error) {
    // Best effort — the backend's reserved_until expiry is the final fallback.
    console.error("Failed to delete stale held appointment:", error);
  }
}

// Payment Provider Selection Modal Component
function PaymentProviderModal({
  isOpen,
  onClose,
  onSelectOption,
  loading,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSelectOption: (option: PaymentOption) => void;
  loading: boolean;
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white w-full sm:max-w-md sm:rounded-lg rounded-t-2xl p-6 animate-in slide-in-from-bottom sm:slide-in-from-bottom-0 duration-200">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-grey-900">
            How would you like to pay?
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-grey-100 rounded-full transition-colors"
            disabled={loading}
          >
            <X className="h-5 w-5 text-grey-500" />
          </button>
        </div>

        <div className="space-y-3">
          <button
            onClick={() => onSelectOption("transfer")}
            disabled={loading}
            className="w-full flex items-center gap-4 p-4 border border-grey-200 rounded-lg hover:bg-grey-50 hover:border-green-300 transition-colors disabled:opacity-50"
          >
            <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center">
              <Wallet className="h-6 w-6 text-blue-600" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-medium text-grey-900">Transfer</p>
            </div>
          </button>

          <button
            onClick={() => onSelectOption("card")}
            disabled={loading}
            className="w-full flex items-center gap-4 p-4 border border-grey-200 rounded-lg hover:bg-grey-50 hover:border-green-300 transition-colors disabled:opacity-50"
          >
            <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center">
              <CreditCard className="h-6 w-6 text-green-600" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-medium text-grey-900">Card</p>
            </div>
          </button>

          <button
            onClick={() => onSelectOption("bank")}
            disabled={loading}
            className="w-full flex items-center gap-4 p-4 border border-grey-200 rounded-lg hover:bg-grey-50 hover:border-green-300 transition-colors disabled:opacity-50"
          >
            <div className="w-12 h-12 bg-purple-50 rounded-full flex items-center justify-center">
              <Building2 className="h-6 w-6 text-purple-600" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-medium text-grey-900">Bank</p>
            </div>
          </button>

          <button
            onClick={() => onSelectOption("ussd")}
            disabled={loading}
            className="w-full flex items-center gap-4 p-4 border border-grey-200 rounded-lg hover:bg-grey-50 hover:border-green-300 transition-colors disabled:opacity-50"
          >
            <div className="w-12 h-12 bg-orange-50 rounded-full flex items-center justify-center">
              <Hash className="h-6 w-6 text-orange-600" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-medium text-grey-900">USSD</p>
            </div>
          </button>

          <button
            onClick={() => onSelectOption("pocket_app")}
            disabled={loading}
            className="w-full flex items-center gap-4 p-4 border border-grey-200 rounded-lg hover:bg-grey-50 hover:border-green-300 transition-colors disabled:opacity-50"
          >
            <div className="w-12 h-12 rounded-full flex items-center justify-center overflow-hidden">
              <img src="/pocket.png" alt="PocketApp" className="w-10 h-10 object-contain" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-medium text-grey-900">PocketApp</p>
            </div>
          </button>

        </div>

        {loading && (
          <div className="mt-4 flex items-center justify-center gap-2 text-grey-600">
            <div className="w-4 h-4 border-2 border-grey-300 border-t-green-500 rounded-full animate-spin" />
            <span className="text-sm">Processing...</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Payment() {
  useScrollToTop();
  const {
    selectedServices,
    selectedStaff,
    appointmentDateTime,
    businessInfo,
    selectedSalon,
    serviceClient,
    handleNextStep,
    addToast,
    priceSummary,
    setPriceSummary,
    promoCode,
    setBookedAppointment,
    bookedAppointment,
    deleteHeldAppointment,
    locationType,
    voucherToBeRedeemed,
    setAppointmentPaymentType,
    setActualAmountPaid,
    setActualProcessingFee,
    // Workflow form
    workflowFormAnswers,
    workflowFormSubmissionId,
    clearWorkflowForm,
    // Availability polling and timer
    selectedDate,
    selectedTime,
    clearSelectedDateTime,
    slotSelectionTime,
  } = useBooking();

  // Workflow form API hooks
  const { triggerAfterAppointment } = useTriggerFormAfterAppointment();
  const { submitAnswers: submitWorkflowAnswers } = useSubmitWorkflowAnswers();

  // Callback when slot becomes unavailable during polling
  const handleSlotUnavailable = useCallback(() => {
    clearSelectedDateTime();
    addToast({
      message: "Sorry, this time slot is no longer available. Please select another time.",
      variant: "warning",
    });
    handleNextStep(BookingStep.TIME_AND_DATE);
  }, [clearSelectedDateTime, addToast, handleNextStep]);

  // Callback when timer expires
  const handleTimerExpire = useCallback(() => {
    clearSelectedDateTime();
    addToast({
      message: "Your slot reservation has expired. Please select a new time.",
      variant: "warning",
    });
    handleNextStep(BookingStep.TIME_AND_DATE);
  }, [clearSelectedDateTime, addToast, handleNextStep]);

  // Add state for payment provider modal
  const [showProviderModal, setShowProviderModal] = useState(false);

  // Calculate base amount from selected services if priceSummary not available
  const calculateBaseAmount = () => {
    if (priceSummary?.totalPaid) {
      return priceSummary.totalPaid;
    }
    // Fallback to calculating from selected services
    return selectedServices.reduce((sum, service) => {
      const price = service.staffPrice || service.price || 0;
      const quantity = service.quantity || 1;
      return sum + price * quantity;
    }, 0);
  };

  const fullAmount = calculateBaseAmount();
  const totalAmountAtTheLocation =
    fullAmount - (priceSummary?.processingFeeAmount || 0);
  // Use deposit amount from backend price summary if available
  const depositAmount = priceSummary?.depositAmount || 0;

  // Client wallet and membership data from phone lookup
  const clientWalletBalance = (serviceClient as any)?.walletBalance || 0;
  const membershipWalletBalance =
    (serviceClient as any)?.membershipWalletBalance || 0;
  const membershipStatus = (serviceClient as any)?.clientMembership
    ?.membershipStatus;
  const membershipServices =
    (serviceClient as any)?.clientMembership?.membership?.services?.map(
      (s: any) => s.id,
    ) || [];
  const membershipRewardPercentage =
    (serviceClient as any)?.clientMembership?.membership?.rewardPercentage || 0;

  // Calculate if service has membership discount
  const getServicePrice = (service: any) => {
    const basePrice = service.staffPrice || service.price || 0;
    const isCoveredByMembership =
      membershipStatus === "live" &&
      membershipServices.includes(service.id) &&
      membershipRewardPercentage > 0;

    if (isCoveredByMembership) {
      const discountAmount = basePrice * (membershipRewardPercentage / 100);
      return basePrice - discountAmount;
    }

    return basePrice;
  };

  const getServiceOriginalPrice = (service: any) => {
    return service.staffPrice || service.price || 0;
  };

  const isServiceCoveredByMembership = (serviceId: string) => {
    return (
      membershipStatus === "live" &&
      membershipServices.includes(serviceId) &&
      membershipRewardPercentage > 0
    );
  };

  // Check if selected services are covered by membership
  const selectedServiceIds = selectedServices.map((s) => s.id);
  const hasServiceInMembership = selectedServiceIds.some((id) =>
    membershipServices.includes(id),
  );
  const allServicesInMembership = selectedServiceIds.every((id) =>
    membershipServices.includes(id),
  );

  // Determine payment method availability
  const membershipIsActive =
    membershipStatus === "live" &&
    membershipWalletBalance > 0 &&
    hasServiceInMembership;
  const canPayWithMembershipWallet =
    membershipIsActive &&
    membershipWalletBalance >= (priceSummary?.totalPaid || fullAmount) &&
    allServicesInMembership;
  const canPayWithClientWallet =
    clientWalletBalance >= (priceSummary?.totalPaid || fullAmount);

  // Hide regular payment options if membership wallet covers the full amount
  const hideRegularPaymentOptions = canPayWithMembershipWallet;

  // Set initial payment method - membership wallet takes priority if available
  const getInitialPaymentMethod = (): PaymentMethod => {
    if (canPayWithMembershipWallet) {
      return "membership_wallet";
    }
    return "card";
  };

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(
    getInitialPaymentMethod(),
  );
  const [loading, setLoading] = useState(false);
  const [agreedToPolicy, setAgreedToPolicy] = useState(false);
  const [paymentProvider, setPaymentProvider] =
    useState<PaymentProvider>("paystack");
  const [paystackLoaded, setPaystackLoaded] = useState(false);
  const [pocketLoaded, setPocketLoaded] = useState(false);

  // Availability polling - stops when loading (user clicked Continue)
  useAvailabilityPolling({
    salonId: selectedSalon?.id || null,
    services: selectedServices,
    staffId: selectedStaff?.id || null,
    selectedDate: selectedDate,
    selectedTime: selectedTime,
    enabled: !!selectedTime && !!selectedDate && !loading,
    pollingInterval: 60000,
    onSlotUnavailable: handleSlotUnavailable,
  });

  // Slot reservation timer - stops when loading (user clicked Continue)
  const { formattedTime, percentRemaining } = useSlotReservationTimer({
    durationMs: 10 * 60 * 1000, // 10 minutes
    onExpire: handleTimerExpire,
    enabled: !!slotSelectionTime && !loading,
    startTime: slotSelectionTime,
  });

  // Load both payment provider scripts on mount
  useEffect(() => {
    // Load Paystack (only if not already loaded)
    if (!document.querySelector('script[src*="js.paystack.co"]')) {
      const paystackScript = document.createElement("script");
      paystackScript.src = "https://js.paystack.co/v2/inline.js";
      paystackScript.async = true;
      paystackScript.onload = () => setPaystackLoaded(true);
      document.body.appendChild(paystackScript);
    } else if (window.PaystackPop) {
      setPaystackLoaded(true);
    }

    // Load Pocket (only if not already loaded)
    if (!document.querySelector('script[src*="pocket-checkout-sdk"]')) {
      const pocketScript = document.createElement("script");
      pocketScript.src =
        "https://pocket-checkout-sdk.netlify.app/checkout-sdk.js";
      pocketScript.async = true;
      pocketScript.onload = () => setPocketLoaded(true);
      document.body.appendChild(pocketScript);
    } else if ((window as any).Pocket) {
      setPocketLoaded(true);
    }
  }, []);

  const paystackKey = import.meta.env.VITE_PAYSTACK_PUBLIC_KEY || "";

  // Ref to track the created appointment for cleanup when payment modal is dismissed.
  // We can't rely on `bookedAppointment` state because setBookedAppointment is async
  // and the payment modal may block React's render cycle.
  const createdAppointmentRef = useRef<any>(null);

  // GraphQL mutations
  const [createAppointmentMutation] = useCreateAppointment();
  const [previewAppointment] = useAppointmentPreview();

  // Call appointment preview when component mounts to get membership discounts
  useEffect(() => {
    const fetchPriceSummary = async () => {
      if (
        !serviceClient ||
        !appointmentDateTime ||
        selectedServices.length === 0
      )
        return;
      if (!selectedSalon?.id) return;
      if (!serviceClient.phone || !serviceClient.countryCode) return;

      // Create appointment services data with startAt for each service
      let currentStartTime = appointmentDateTime;
      const services = selectedServices.map((service, index) => {
        if (index > 0) {
          const prevService = selectedServices[index - 1];
          const prevDuration = prevService.duration || 30;
          const prevStartDate = new Date(currentStartTime);
          prevStartDate.setMinutes(prevStartDate.getMinutes() + prevDuration);
          currentStartTime = prevStartDate.toISOString();
        }

        // Extract original service ID and variant ID for appointment creation
        let originalServiceId = service.id;
        let serviceVariantId = null;

        if (service.variantId && service.id.includes("-")) {
          // This is a variant, extract the original service ID
          originalServiceId = service.id.slice(
            0,
            -(service.variantId.length + 1),
          ); // +1 for the dash
          serviceVariantId = service.variantId;
        }

        return {
          serviceId: originalServiceId,
          ...(serviceVariantId && { serviceVariantId }),
          price: service.price,
          staffIds: service.staff
            ? [service.staff.id]
            : selectedStaff
              ? [selectedStaff.id]
              : [],
          quantity: service.quantity || 1,
          startAt: currentStartTime,
        };
      });

      const requestData = {
        salonId: selectedSalon?.id,
        client: {
          phone: serviceClient.phone,
          countryCode: serviceClient.countryCode,
        },
        services,
        paymentMethod: "card",
        ...(promoCode && { promoCode }),
      };

      try {
        const { data } = await previewAppointment({
          input: requestData,
        });

        if (data?.appointmentPreview?.errors?.length > 0) {
          // Handle GraphQL errors
          const errorMessage =
            data.appointmentPreview.errors[0]?.message ||
            "Failed to calculate pricing";
          addToast({
            message: errorMessage,
            variant: "error",
          });
          return;
        }

        if (data?.appointmentPreview?.data) {
          const preview = data.appointmentPreview.data;

          setPriceSummary(preview);
        }
      } catch (error) {
        addToast({
          message:
            "Unable to load pricing information. Please refresh the page.",
          variant: "error",
        });
      }
    };

    fetchPriceSummary();
  }, [
    serviceClient,
    appointmentDateTime,
    selectedServices,
    selectedSalon,
    promoCode,
  ]);

  // Check if providers are already loaded (e.g., from cache)
  useEffect(() => {
    if (window.PaystackPop) {
      setPaystackLoaded(true);
    }
    if ((window as any).Pocket) {
      setPocketLoaded(true);
    }
  }, []);

  // Update payment method when membership wallet becomes available
  useEffect(() => {
    if (canPayWithMembershipWallet && paymentMethod !== "membership_wallet") {
      setPaymentMethod("membership_wallet");
    }
  }, [canPayWithMembershipWallet]);

  const validateBooking = () => {
    if (!agreedToPolicy && businessInfo?.cancellationPolicy) {
      addToast({
        message: "Please agree to the booking policy",
        variant: "error",
      });
      return false;
    }

    if (
      !serviceClient ||
      !appointmentDateTime ||
      selectedServices.length === 0
    ) {
      addToast({
        message: "Missing booking information",
        variant: "error",
      });
      return false;
    }

    return true;
  };

  // Submit workflow form answers after appointment creation
  const submitWorkflowFormAnswers = async (appointmentId: string) => {
    // Only submit if we have answers and a submission ID
    if (
      !workflowFormSubmissionId ||
      Object.keys(workflowFormAnswers).length === 0
    ) {
      return;
    }

    try {
      await submitWorkflowAnswers({
        submissionId: workflowFormSubmissionId,
        answers: workflowFormAnswers,
        phone: `${serviceClient?.callingCode}${serviceClient?.phone}`,
        clientId: serviceClient?.id || "",
        appointmentId: appointmentId,
      });

      // Clear workflow form state after successful submission
      clearWorkflowForm();
    } catch (error) {
      // Silently fail - don't block the booking flow
      console.error("Error submitting workflow form answers:", error);
    }
  };

  // Trigger after-booking workflow form (AFTER_BOOKING_CONFIRMED)
  const triggerAfterBookingForm = async (
    appointmentId: string,
    clientId?: string
  ) => {
    if (!businessInfo?.id || !clientId) return;

    try {
      const serviceIds = selectedServices.map((service) => {
        // Extract original service ID if it's a variant
        if (service.variantId && service.id.includes("-")) {
          return service.id.slice(0, -(service.variantId.length + 1));
        }
        return service.id;
      });

      await triggerAfterAppointment({
        businessId: businessInfo.id,
        services: serviceIds,
        clientId: clientId,
        sendCondition: "AFTER_BOOKING_CONFIRMED",
        appointmentId: appointmentId,
      });
    } catch (error) {
      // Silently fail - after-booking forms are optional
      console.error("Error triggering after-booking form:", error);
    }
  };

  const handlePaymentSuccess = async (reference: string) => {
    // Clear the ref BEFORE navigating so that if the payment provider fires
    // onClose after onSuccess (Paystack and Pocket both do this),
    // handlePaymentClose will see a null ref and skip the deletion.
    createdAppointmentRef.current = null;
    clearHeldAppointment();

    addToast({
      message: "Payment successful! Your appointment has been booked.",
      variant: "success",
    });

    // Move to success step
    handleNextStep(BookingStep.SUCCESS);
  };

  // Delete the held appointment (if any) to release the reserved time slot.
  // Use the ref because bookedAppointment state may not have updated yet
  // (setBookedAppointment is async and the payment modal can block rendering).
  const cleanupHeldAppointment = () => {
    const appointment = createdAppointmentRef.current;
    if (appointment && selectedSalon?.id) {
      deleteHeldAppointment(appointment, selectedSalon.id);
      createdAppointmentRef.current = null;
    }
    clearHeldAppointment();
    setLoading(false);
  };

  const handlePaymentClose = () => {
    addToast({
      message: "Payment cancelled",
      variant: "error",
    });
    cleanupHeldAppointment();
  };

  // Initiate Paystack payment - uses regular checkout
  // Apple Pay is mounted separately on page load
  const initiatePaystackPayment = async (responseData: any, channels?: string[]) => {
    if (!window.PaystackPop) {
      console.error("Paystack not loaded");
      addToast({
        message: "Payment system not ready. Please try again.",
        variant: "error",
      });
      cleanupHeldAppointment();
      return;
    }

    const paymentAuth = responseData.paymentAuthorization;
    if (!paymentAuth?.accessCode) {
      console.error("Missing payment authorization access code:", paymentAuth);
      addToast({
        message: "Payment authorization failed. Please try again.",
        variant: "error",
      });
      cleanupHeldAppointment();
      return;
    }

    if (!paystackKey) {
      addToast({
        message: "Payment system not configured. Please contact support.",
        variant: "error",
      });
      cleanupHeldAppointment();
      return;
    }

    // Use amountToPay from backend response which includes processing fee
    const amountToPay = responseData.amountToPay || priceSummary?.totalPaid || fullAmount;

    try {
      // Use regular Paystack checkout for card payments
      // Apple Pay button is already mounted separately on page load
      const paystackPop = new window.PaystackPop();
      await paystackPop.checkout({
        key: paystackKey,
        email: serviceClient?.email || "",
        amount: Math.round(amountToPay * 100),
        currency: businessInfo?.country?.currency[0]?.code || "NGN",
        accessCode: paymentAuth.accessCode,
        ...(channels && { channels }),
        onSuccess: (transaction: { reference: string }) => {
          handlePaymentSuccess(transaction.reference);
        },
        onCancel: () => {
          handlePaymentClose();
        },
        onClose: () => {
          handlePaymentClose();
        },
      });
    } catch (error) {
      console.error("Paystack checkout error:", error);
      addToast({
        message: "Failed to initiate payment. Please try again.",
        variant: "error",
      });
      cleanupHeldAppointment();
    }
  };

  const initiatePocketPayment = (responseData: any, paymentType: string) => {
    const Pocket = (window as any).Pocket;
    if (!Pocket) {
      console.error("Pocket SDK not loaded");
      addToast({
        message: "Payment system not ready. Please try again.",
        variant: "error",
      });
      cleanupHeldAppointment();
      return;
    }

    const pocketKey = import.meta.env.VITE_POCKET_PUBLIC_KEY;
    if (!pocketKey) {
      addToast({
        message: "Payment system not configured. Please contact support.",
        variant: "error",
      });
      cleanupHeldAppointment();
      return;
    }

    // Generate a unique reference for Pocket (Pocket doesn't use backend payment authorization)
    const appointment = responseData.appointment;
    const reference = `PKT-${appointment?.id || Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

    const pocketBusinessId = import.meta.env.VITE_POCKET_BUSINESS_ID;
    const pocketWalletId = import.meta.env.VITE_POCKET_WALLET_ID;
    const processingFeeAmount = responseData.processingFeeAmount || 0;
    
    // Use amountToPay from backend response which includes processing fee
    const amountToPay = responseData.amountToPay || fullAmount;

    try {
      // Initialize Pocket checkout with popup view
      const pocketConfig: any = {
        key: pocketKey,
        business_id: pocketBusinessId,
        wallet_id: pocketWalletId,
        amount: Math.round(amountToPay * 100), // Convert to kobo (smallest currency unit)
        reference: reference,
        narration: `Payment for ${selectedServices.length} service(s) at ${businessInfo?.name || "salon"}`,
        view: "popup",
        meta: {
          purpose: "appointment",
          appointment_id: appointment?.id,
          business_id: selectedSalon?.id,
          payment_type: paymentType,
          processing_fee_amount: processingFeeAmount,
        },
      };
      
      // Add callbacks
      Object.assign(pocketConfig, {
        onSuccess: (response: any) => {
          console.log("Pocket onSuccess:", response);
          handlePaymentSuccess(response.reference);
        },
        onClose: () => {
          console.log("Pocket onClose");
          handlePaymentClose();
        },
        onPending: () => {
          console.log("Pocket onPending");
          addToast({
            message:
              "Payment is being processed. Please wait for confirmation.",
            variant: "warning",
          });
        },
        onError: (error: any) => {
          console.error("Pocket onError:", error);
          addToast({
            message: error?.message || "Payment failed. Please try again.",
            variant: "error",
          });
          cleanupHeldAppointment();
        },
      });

      console.log("Initializing Pocket with config:", pocketConfig);
      const payWithPocket = new Pocket(pocketConfig);

      payWithPocket.show();
    } catch (error) {
      addToast({
        message: "Failed to initiate payment. Please try again.",
        variant: "error",
      });
      cleanupHeldAppointment();
    }
  };

  const initiatePayment = (responseData: any, provider: PaymentProvider, paymentType: string, channels?: string[]) => {
    switch (provider) {
      case "paystack":
        initiatePaystackPayment(responseData, channels);
        break;
      case "pocket":
        initiatePocketPayment(responseData, paymentType);
        break;
      default:
        addToast({
          message: "Invalid payment provider selected",
          variant: "error",
        });
        setLoading(false);
    }
  };

  const createAppointment = async (
    paymentType:
      | "full_payment"
      | "deposit_payment"
      | "balance_payment"
      | "unpaid",
    selectedProvider?: PaymentProvider,
    channels?: string[],
  ) => {
    // Use the passed provider or fall back to state
    const providerToUse = selectedProvider || paymentProvider;
    if (!validateBooking()) return;

    if (!appointmentDateTime || !serviceClient) return;

    // If a previous appointment was held but never cleaned up (e.g. the user
    // closed the tab mid-checkout), delete it now before creating a new one.
    await deleteStaleHeldAppointment();

    setLoading(true);

    try {
      const services = selectedServices.map((service) => {
        // Extract original service ID and variant ID for appointment creation
        let originalServiceId = service.id;
        let serviceVariantId = null;

        if (service.variantId && service.id.includes("-")) {
          // This is a variant, extract the original service ID
          originalServiceId = service.id.slice(
            0,
            -(service.variantId.length + 1),
          ); // +1 for the dash
          serviceVariantId = service.variantId;
        }

        const serviceData: any = {
          serviceId: originalServiceId,
          ...(serviceVariantId && { serviceVariantId }),
          quantity: service.quantity || 1,
          staffId: service.staff?.id || selectedStaff?.id,
        };
        return serviceData;
      });

      let appointmentPaymentMethod = voucherToBeRedeemed ? "voucher" : "card";
      if (voucherToBeRedeemed) {
        appointmentPaymentMethod = "voucher";
      } else {
        if (paymentType === "balance_payment") {
          appointmentPaymentMethod = "unpaid";
        } else if (paymentMethod === "wallet") {
          appointmentPaymentMethod = "client_wallet";
        } else if (paymentMethod === "membership_wallet") {
          appointmentPaymentMethod = "membership_wallet";
        } else if (paymentMethod === "unpaid") {
          appointmentPaymentMethod = "unpaid";
        }
      }

      // Build split payments for membership wallet
      let splitPayments: Array<{
        splitPaymentMode: string;
        amount: number;
      }> = [];

      // If paying with membership wallet, add split payment to properly debit the wallet
      if (paymentMethod === "membership_wallet" && canPayWithMembershipWallet) {
        const amountToPay = priceSummary?.totalPaid || fullAmount;
        splitPayments = [
          {
            splitPaymentMode: "membership_wallet",
            amount: amountToPay,
          },
        ];
      }

      const appointmentData: any = {
        salonId: selectedSalon?.id,
        startAt: appointmentDateTime,
        services,
        paymentType,
        appointmentPaymentMethod,
        paymentProvider: providerToUse, // Pass the selected payment provider (paystack or pocket)
        ...(channels && { channels }), // Restrict Paystack to specific payment channels
        locationType: locationType || "salon",
        address:
          locationType === "home" || locationType === "flexible"
            ? serviceClient?.address
            : "",
        source: "public",
        ...(promoCode && { promoCode }),
        note: serviceClient?.note || "",
        ...(voucherToBeRedeemed && {
          voucherId: voucherToBeRedeemed.id,
          voucherRedemptionMode: "services",
        }),
        // Include split payments for membership wallet payments
        ...(splitPayments.length > 0 && { splitPayments }),
      };

      // Always send clientInput (backend will find/update client by phone)
      appointmentData.clientInput = {
        firstName: serviceClient?.firstName || "",
        lastName: serviceClient?.lastName || "",
        email: serviceClient?.email || "",
        phone: serviceClient?.phone || "",
        countryCode: serviceClient?.countryCode || "NG",
        callingCode: serviceClient?.callingCode || "+234",
      };

      // Add address at the appointment level if it's a home service
      if (serviceClient?.address && selectedSalon?.locationType === "home") {
        appointmentData.address = serviceClient.address;
      }

      const { data } = await createAppointmentMutation({
        input: appointmentData,
      });

      if (data?.createAppointment?.status === 201) {
        const responseData = data.createAppointment;
        const appointment = responseData.appointment;
        const paymentAuth = responseData.paymentAuthorization;
        const shouldInitiatePayment = responseData.initiatePayment;
        setBookedAppointment(appointment);
        createdAppointmentRef.current = appointment;
        // Persist to localStorage so we can clean up if the user closes the tab
        if (appointment?.id && selectedSalon?.id) {
          saveHeldAppointment(appointment.id, selectedSalon.id);
        }
        // Store the payment type so Success page knows if it was deposit, full payment, or unpaid
        // If user selected "Pay at Venue", mark as unpaid even though API receives full_payment
        setAppointmentPaymentType(paymentMethod === "unpaid" ? "unpaid" : paymentType);
        // Store actual amount charged and processing fee from backend response
        // so the confirmation page can display them accurately
        setActualAmountPaid(responseData.amountToPay || null);
        setActualProcessingFee(responseData.processingFeeAmount || null);

        // Submit workflow form answers if there are any
        await submitWorkflowFormAnswers(appointment.id);

        // Trigger after-booking workflow form
        await triggerAfterBookingForm(appointment.id, appointment.client?.id);

        if (paymentAuth && shouldInitiatePayment) {
          initiatePayment(responseData, providerToUse, paymentType, channels);
        } else {
          addToast({
            message: "Appointment booked successfully!",
            variant: "success",
          });
          handleNextStep(BookingStep.SUCCESS);
        }
      } else {
        const errorMessage =
          data?.createAppointment?.errors?.[0]?.message ||
          "Failed to create appointment";
        addToast({
          message: errorMessage,
          variant: "error",
        });
        setLoading(false);
      }
    } catch (error) {
      addToast({
        message: "Failed to create appointment. Please try again.",
        variant: "error",
      });
      setLoading(false);
    }
  };

  const handlePaymentSubmit = (selectedMethod: PaymentMethod) => {
    setPaymentMethod(selectedMethod);

    if (selectedMethod === "card" || voucherToBeRedeemed) {
      createAppointment("full_payment");
    } else if (selectedMethod === "deposit") {
      createAppointment("deposit_payment");
    } else if (
      selectedMethod === "wallet" ||
      selectedMethod === "membership_wallet"
    ) {
      createAppointment("full_payment");
    } else if (selectedMethod === "unpaid") {
      createAppointment("full_payment");
    } else {
      createAppointment("full_payment");
    }
  };

  const handleBack = () => {
    handleNextStep(BookingStep.PHONE_FORM);
  };

  // Handle provider selection from modal
  const handleProviderSelect = (option: PaymentOption) => {
    const config = PAYMENT_OPTION_CONFIG[option];
    setPaymentProvider(config.provider);
    setShowProviderModal(false);

    // Check if the selected provider's SDK is loaded
    const isProviderReady =
      config.provider === "paystack"
        ? paystackLoaded || window.PaystackPop
        : pocketLoaded || (window as any).Pocket;

    if (!isProviderReady) {
      addToast({
        message: "Payment provider is still loading. Please try again.",
        variant: "error",
      });
      return;
    }

    // Process the payment with the selected provider (pass provider directly to avoid state timing issues)
    if (paymentMethod === "card") {
      createAppointment("full_payment", config.provider, config.channels);
    } else if (paymentMethod === "deposit") {
      createAppointment("deposit_payment", config.provider, config.channels);
    }
  };

  return (
    <>
      {/* Payment Provider Selection Modal */}
      <PaymentProviderModal
        isOpen={showProviderModal}
        onClose={() => setShowProviderModal(false)}
        onSelectOption={handleProviderSelect}
        loading={loading}
      />

      <div className="min-h-screen bg-grey-5 pb-20 lg:pb-0">
        {/* Header */}

        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="space-y-6">
            {/* Slot Countdown Timer - shows until user clicks Continue */}
            {slotSelectionTime && !loading && (
              <div className="max-w-3xl mx-auto flex justify-center">
                <SlotCountdownTimer
                  formattedTime={formattedTime}
                  percentRemaining={percentRemaining}
                />
              </div>
            )}

            {/* Booking Summary at the top */}
            <div className="max-w-3xl mx-auto">
              <div className="bg-white rounded-lg shadow-sm">
                <CartSummary
                  showDateTime={true}
                  showLocation={true}
                  showMembershipBanner={
                    serviceClient &&
                    serviceClient?.clientMembership &&
                    serviceClient?.clientMembership?.membershipStatus === "live"
                  }
                  showPromoCode={true}
                  customButton={{
                    text: "Continue to booking",
                    onClick: () => {
                      // Skip staff selection if business allows service staff selection
                      const nextStep = businessInfo?.allowServiceStaffSelection
                        ? BookingStep.TIME_AND_DATE
                        : BookingStep.STAFF_SELECTION;
                      handleNextStep(nextStep);
                    },
                    className:
                      "w-full bg-green-300 hover:bg-green-600 text-white",
                  }}
                />
              </div>
            </div>

            {/* Payment Options below */}
            <div className="max-w-3xl mx-auto space-y-4">
              {/* Membership Status Banner */}
              {(serviceClient as any)?.clientMembership &&
              serviceClient?.clientMembership?.membershipStatus === "live" &&
              priceSummary?.loyaltyDiscountAmount &&
              priceSummary.loyaltyDiscountAmount > 0 ? (
                <div className="bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-300 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Crown className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-amber-900 font-medium">
                        {
                          (serviceClient as any).clientMembership.membership
                            .name
                        }{" "}
                        Member Discount Applied!
                      </p>
                      <p className="text-amber-700 text-sm mt-1">
                        You're saving{" "}
                        {formatPrice(
                          priceSummary.loyaltyDiscountAmount,
                          businessInfo?.country?.currency[0]?.symbol || "₦",
                        )}{" "}
                        on this booking
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Booking Policy */}
              {businessInfo?.cancellationPolicy ? (
                <div className="bg-white rounded-lg">
                  <h3 className="text-lg font-semibold text-grey-900 mb-4">
                    Booking Policy
                  </h3>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 text-sm text-amber-800">
                        <h3 className="font-medium text-amber-900 mb-2 flex items-center gap-2">
                          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                          <p> Please review our policy before proceeding</p>
                        </h3>
                        {/* Display policy as paragraphs/bulleted lines */}
                        <div
                          className="rich-text-content prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{
                            __html: businessInfo.cancellationPolicy,
                          }}
                        />

                        {/* Acceptance controls */}
                        <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
                          {!agreedToPolicy ? (
                            <>
                              <label className="flex items-start gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={agreedToPolicy}
                                  onChange={(e) =>
                                    setAgreedToPolicy(e.target.checked)
                                  }
                                  className="mt-1 w-4 h-4 text-green-300 rounded border-amber-300 focus:ring-amber-300"
                                />
                                <span className="text-sm text-amber-800">
                                  I agree to the cancellation policy and terms
                                </span>
                              </label>
                            </>
                          ) : (
                            <div className="inline-flex items-center gap-2 text-amber-900">
                              <CheckCircle className="h-5 w-5 text-amber-700" />
                              <span className="font-medium">
                                Policy accepted
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="bg-white rounded-lg mt-2 max-h-[70vh] flex flex-col">
                <h2 className="text-lg font-semibold text-grey-900">
                  Select Payment Method
                </h2>
                <div className="pt-4 overflow-y-auto flex-1">
                  <div className="space-y-3">
                    {/* Membership Wallet Payment - Shows when membership covers full amount */}
                    {canPayWithMembershipWallet ? (
                      <label className="flex items-start gap-3 p-4 border border-green-200 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100 transition-colors">
                        <input
                          type="radio"
                          name="payment"
                          value="membership_wallet"
                          checked={paymentMethod === "membership_wallet"}
                          onChange={(e) =>
                            setPaymentMethod(e.target.value as PaymentMethod)
                          }
                          className="mt-1 w-4 h-4 text-green-300 focus:ring-green-300"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Wallet className="h-4 w-4 text-green-600" />
                            <span className="font-medium text-grey-900">
                              Pay with Membership Wallet
                            </span>
                          </div>
                          <p className="text-sm text-green-700">
                            Balance:{" "}
                            {formatPrice(
                              membershipWalletBalance,
                              businessInfo?.country?.currency[0]?.symbol || "₦",
                            )}
                          </p>
                          <p className="text-xs text-green-600 mt-1">
                            No payment required - covered by membership
                          </p>
                        </div>
                      </label>
                    ) : null}

                    {/* Client Wallet Payment - Shows when wallet has sufficient balance */}
                    {canPayWithClientWallet && !hideRegularPaymentOptions ? (
                      <label className="flex items-start gap-3 p-4 border border-grey-100 rounded-lg cursor-pointer hover:bg-grey-50 transition-colors">
                        <input
                          type="radio"
                          name="payment"
                          value="wallet"
                          checked={paymentMethod === "wallet"}
                          onChange={(e) =>
                            setPaymentMethod(e.target.value as PaymentMethod)
                          }
                          className="mt-1 w-4 h-4 text-green-300 focus:ring-green-300"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Wallet className="h-4 w-4 text-grey-600" />
                            <span className="font-medium text-grey-900">
                              Pay with Wallet
                            </span>
                          </div>
                          <p className="text-sm text-grey-600">
                            Balance:{" "}
                            {formatPrice(
                              clientWalletBalance,
                              businessInfo?.country?.currency[0]?.symbol || "₦",
                            )}
                          </p>
                          <p className="text-xs text-green-600 mt-1">
                            Instant payment from wallet
                          </p>
                        </div>
                      </label>
                    ) : null}

                    {/* Card Payment - Hidden if membership wallet covers everything */}
                    {!hideRegularPaymentOptions ? (
                      <label className="flex items-start gap-3 p-4 border border-grey-100 rounded-lg cursor-pointer hover:bg-grey-50 transition-colors">
                        <input
                          type="radio"
                          name="payment"
                          value="card"
                          checked={paymentMethod === "card"}
                          onChange={(e) =>
                            setPaymentMethod(e.target.value as PaymentMethod)
                          }
                          className="mt-1 w-4 h-4 text-green-300 focus:ring-green-300"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <CreditCard className="h-4 w-4 text-grey-600" />
                            <span className="font-medium text-grey-900">
                              Pay Full Amount Online
                            </span>
                          </div>
                          <p className="text-sm text-grey-600">
                            Pay{" "}
                            {formatPrice(
                              priceSummary?.subTotal || fullAmount,
                              businessInfo?.country?.currency[0]?.symbol || "₦",
                            )}{" "}
                            + processing fee
                          </p>
                          <p className="text-xs text-green-600 mt-1">
                            Instant confirmation
                          </p>
                        </div>
                      </label>
                    ) : null}

                    {!hideRegularPaymentOptions && depositAmount > 0 ? (
                      <label className="flex items-start gap-3 p-4 border border-grey-100 rounded-lg cursor-pointer hover:bg-grey-50 transition-colors">
                        <input
                          type="radio"
                          name="payment"
                          value="deposit"
                          checked={paymentMethod === "deposit"}
                          onChange={(e) =>
                            setPaymentMethod(e.target.value as PaymentMethod)
                          }
                          className="mt-1 w-4 h-4 text-green-300 focus:ring-green-300"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Wallet className="h-4 w-4 text-grey-600" />
                            <span className="font-medium text-grey-900">
                              Pay Deposit
                            </span>
                          </div>
                          <p className="text-sm text-grey-600">
                            Pay{" "}
                            {formatPrice(
                              depositAmount,
                              businessInfo?.country?.currency[0]?.symbol || "₦",
                            )}{" "}
                            + processing fee
                          </p>
                          <p className="text-xs text-amber-600 mt-1">
                            Balance due at appointment
                          </p>
                        </div>
                      </label>
                    ) : null}

                    {depositAmount === 0 ? (
                      <label className="flex items-start gap-3 p-4 border border-grey-100 rounded-lg cursor-pointer hover:bg-grey-50 transition-colors">
                        <input
                          type="radio"
                          name="payment"
                          value="unpaid"
                          checked={paymentMethod === "unpaid"}
                          onChange={(e) =>
                            setPaymentMethod(e.target.value as PaymentMethod)
                          }
                          className="mt-1 w-4 h-4 text-green-300 focus:ring-green-300"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Wallet className="h-4 w-4 text-grey-600" />
                            <span className="font-medium text-grey-900">
                              Pay at Venue
                            </span>
                          </div>
                        </div>
                      </label>
                    ) : null}
                    {!voucherToBeRedeemed && (
                      <div className="bg-white py-4">
                        <div className="flex gap-3">
                          <Button
                            onClick={() => {
                              // For card/deposit payments, show provider modal
                              if (
                                paymentMethod === "card" ||
                                paymentMethod === "deposit"
                              ) {
                                setShowProviderModal(true);
                              } else {
                                // For wallet/membership/unpaid, process directly
                                handlePaymentSubmit(paymentMethod);
                              }
                            }}
                            disabled={
                              loading ||
                              (!!businessInfo?.cancellationPolicy &&
                                !agreedToPolicy)
                            }
                            className="flex-1 bg-green-300 text-white disabled:opacity-50"
                          >
                            {loading ? "Processing..." : "Continue"}
                          </Button>
                        </div>
                      </div>
                    )}

                    {voucherToBeRedeemed && (
                      <div className="  bg-white  py-4">
                        <div className="flex gap-3">
                          <Button
                            onClick={() => handlePaymentSubmit("voucher")}
                            disabled={loading}
                            className="flex-1 bg-green-300   text-white disabled:opacity-50"
                          >
                            {loading ? "Processing..." : "Redeem Voucher"}
                          </Button>
                        </div>
                      </div>
                    )}
                    {priceSummary?.depositAmount &&
                    priceSummary.depositAmount > 0 ? (
                      <div>
                        <p className="text-lg font-semibold text-grey-600 mb-2">
                          Summary
                        </p>
                        <div className="border border-12/5 border-grey-100 bg-white rounded-[8px] p-3">
                          <div className="flex justify-between items-center  border-grey-100 mb-2">
                            <span className="text-sm text-grey-600">
                              Subtotal
                            </span>
                            <span className="text-sm text-grey-600">
                              {formatPrice(
                                priceSummary?.subTotal,
                                businessInfo?.country?.currency[0]?.symbol ||
                                  "₦",
                              )}
                            </span>
                          </div>
                          {priceSummary?.taxAmount &&
                          priceSummary.taxAmount > 0 &&
                          selectedSalon?.isTaxVisible ? (
                            <div className="flex justify-between text-sm mb-2">
                              <span className="text-grey-600">
                                Tax{selectedSalon?.taxInclusivePricing ? " (incl.)" : ""}
                              </span>
                              <span className="text-grey-900">
                                {formatPrice(
                                  priceSummary.taxAmount,
                                  businessInfo?.country?.currency[0]?.symbol ||
                                    "₦",
                                )}
                              </span>
                            </div>
                          ) : null}
                          <div className="flex justify-between items-center border-grey-100 ">
                            <span className="text-sm text-grey-600">Total</span>
                            <span className="text-sm text-grey-600">
                              {formatPrice(
                                (priceSummary?.subTotal || 0) + 
                                (selectedSalon?.isTaxVisible && !selectedSalon?.taxInclusivePricing ? (priceSummary?.taxAmount || 0) : 0),
                                businessInfo?.country?.currency[0]?.symbol ||
                                  "₦",
                              )}
                            </span>
                          </div>
                          <p className="text-xs text-grey-500 mt-2">
                            + processing fee (based on payment method)
                          </p>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
